import { useEffect, useState } from "react";
import { BaseButton } from "src/components";
import { useModal } from "src/recoil/modalState/hook";
import useModuSign from "../../hooks/useModuSign";
import ModuSignAddModal from "./ModuSignAddModal";
import ModuSignContractTable from "./ModuSignContractTable";
import { ModuSignCancelDocument, ModuSignDocument, ModuSignStartDocumentRequest } from "src/api/contract/modusign-types";
import ModuSignDocumentCancel from "./components/ModuSignDocumentCancel";
import moment from "moment";

type Props = {
  viewOnly?: boolean;
  setEditable?: (bool: boolean) => void;
  onChangeContractSignType?: (contractSignType: { directContract: boolean; isEsign: boolean; isModuSign: boolean }) => void;
};

const ModuSignContractSection = ({ viewOnly = false, setEditable, onChangeContractSignType }: Props) => {
  const [moduSignDocumentList, setModuSignDocumentList] = useState<ModuSignDocument[]>([]);

  const { setAbstractModalZ1, setBaseModal } = useModal();
  const {
    fetchModuSignDocumentList, //
    onCancelModuSignDocument,
    resendModuSignDocument,
    openModuSignDocumentDetail,
    onSaveModuSignDocument, //
    fetchModuSignDocumentPreview,
  } = useModuSign();

  useEffect(() => {
    getModuSignDocumentList();
  }, []);

  const getModuSignDocumentList = async () => {
    try {
      const moduSignDocs = await fetchModuSignDocumentList();
      if (moduSignDocs) {
        setModuSignDocumentList(moduSignDocs.documents);
      }
    } catch (error: any) {
      if (error?.errorCode === "eMS429") {
        setBaseModal({
          isOpen: true,
          title: `모두싸인 API 최대 허용 요청 수를 초과하였습니다. ${error.retryAfter}초 후에 재시도 해주세요.`,
          btnRightTitle: "확인",
          onClick: () => setBaseModal({ isOpen: false }),
        });
      }
    }
  };

  const handleCancelModuSignDocument = async (cancelDocument: ModuSignCancelDocument) => {
    try {
      await onCancelModuSignDocument(cancelDocument);
      // 성공하면 여기로 온다
      await getModuSignDocumentList();
      setAbstractModalZ1({ isOpen: false });
    } catch (error) {
      // 실패하면 여기로 온다
      console.error("문서 취소 실패:", error);
      // 에러 처리 (토스트 메시지 등)
    }
  };

  // 모두싸인 문서 미리보기 페이지로 이동
  const openModuSignDocumentPreview = async (moduSignStartDocument: ModuSignStartDocumentRequest) => {
    const transformedData = transformModuSignRequestData(moduSignStartDocument);

    try {
      const previewData = await fetchModuSignDocumentPreview(transformedData);
      // previewData가 바로 ModuSignDocumentPreview 타입
      window.open(previewData.previewUrl, "_blank");
    } catch (error) {
      console.error("문서 미리보기 실패:", error);
    }
  };

  // 폼 제출 핸들러
  const onSubmit = async (data: ModuSignStartDocumentRequest) => {
    const transformedData = transformModuSignRequestData(data);
    try {
      await onSaveModuSignDocument(transformedData);
      setAbstractModalZ1({ isOpen: false });
      onChangeContractSignType?.({ directContract: true, isEsign: false, isModuSign: false });
      setEditable?.(false);
    } catch (error) {
      console.error("문서 전송 실패:", error);
    }
  };

  // 모두싸인 템플릿 추가 모달 열기
  const openModuSignAddModal = () => {
    setAbstractModalZ1({
      isOpen: true,
      children: (
        <ModuSignAddModal
          onClose={() => setAbstractModalZ1({ isOpen: false })}
          setEditable={setEditable}
          onSubmit={onSubmit}
          openModuSignDocumentPreview={openModuSignDocumentPreview}
        />
      ),
    });
  };

  // 모두싸인 문서 취소 모달 열기
  const openModuSignDocumentCancelModal = (document: ModuSignDocument) => {
    setAbstractModalZ1({
      isOpen: true,
      size: "medium",
      children: (
        <ModuSignDocumentCancel
          document={document} //
          onClose={() => setAbstractModalZ1({ isOpen: false })}
          onSubmit={handleCancelModuSignDocument}
        />
      ),
    });
  };

  // 모두싸인 문서 재전송 요청
  const resendModuSignDocsRequest = async (documentId: string) => {
    const execute = async () => {
      try {
        await resendModuSignDocument(documentId);
        // 성공하면 여기로 온다
        await getModuSignDocumentList();
      } catch (error) {
        // 실패하면 여기로 온다
        console.error("문서 재전송 실패:", error);
      }
    };

    setBaseModal({
      isOpen: true,
      title: "재전송하시겠습니까?",
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
      onClose: () => setBaseModal({ isOpen: false }),
      onClick: () => {
        execute();
        setBaseModal({ isOpen: false });
      },
    });
  };

  // 모두싸인 문서 상세 조회 > 문서 다운로드 url 열기
  const openModuSignDocument = async (documentId: string, type: "file" | "auditTrail") => {
    try {
      const documentInfo = await openModuSignDocumentDetail(documentId);
      // 성공하면 documentInfo가 바로 ModuSignDocument 타입
      if (type === "file") {
        window.open(documentInfo.file.downloadUrl, "_blank");
      } else {
        window.open(documentInfo.auditTrail.downloadUrl, "_blank");
      }
    } catch (error) {
      console.error("문서 상세 조회 실패:", error);
    }
  };

  // 공통 데이터 변환 함수
  const transformModuSignRequestData = (data: ModuSignStartDocumentRequest): ModuSignStartDocumentRequest => {
    return {
      ...data,
      document: {
        ...data.document,
        participantMappingsList: data.document.participantMappingsList.map((participant) => {
          // 현재 시점
          const now = moment();
          // 목표 날짜의 23:59:59로 설정
          const targetDate = moment(participant.signingDuration).endOf("day");
          // 분 단위 차이 계산
          const diffInMinutes = targetDate.diff(now, "minutes");

          return {
            ...participant,
            signingDuration: diffInMinutes,
            signingMethod: {
              type: participant.signingMethod.type,
              value:
                participant.signingMethod.type === "EMAIL" ? participant.signingMethod.value : participant.signingMethod.value.replaceAll("-", ""),
            },
            excluded: false,
          };
        }),
      },
    };
  };

  return (
    <section className="contents-container__grid">
      <div className="contents-container__grid-index">
        <p className={!viewOnly ? "required" : ""}>계약서</p>
      </div>
      <div className="contents-container__grid-contents">
        {!viewOnly && (
          <div>
            <BaseButton title="전자서명 바로가기" className="color-white" onClick={openModuSignAddModal} />
          </div>
        )}
        <div className="mt10">
          <ModuSignContractTable
            moduSignDocumentList={moduSignDocumentList}
            openCancelModal={openModuSignDocumentCancelModal}
            resendModuSignDocsRequest={resendModuSignDocsRequest}
            onDownloadDocument={openModuSignDocument}
          />
        </div>
      </div>
    </section>
  );
};

export default ModuSignContractSection;
